/**
 * Decision Room → Forms section, SELLER side (2026-09-07, flag `forms`).
 *
 * Which forms this buyer sees in the room, and what they answered.
 * Assigning a form flips the room's `forms` section visible on the
 * server (backend POST /api/forms/:id/rooms), so the parent reloads
 * the room after every change and the sidebar eye-toggle tells the
 * truth. Editing the form itself lives on the Forms page.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { apiRequest, formatWhen } from '../utils/apiRequest';

export default function RoomFormsSection({ authToken, spaceId, onReload }) {
  const [assigned, setAssigned] = useState(null);
  const [library, setLibrary] = useState([]);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [responses, setResponses] = useState({}); // formId → { form, responses }

  const load = useCallback(async () => {
    try {
      const [room, all] = await Promise.all([
        apiRequest(`/api/forms/room/${spaceId}`, { token: authToken }),
        apiRequest('/api/forms', { token: authToken }),
      ]);
      setAssigned(room.forms || []);
      setLibrary((all.forms || []).filter((f) => f.status === 'active'));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [authToken, spaceId]);

  useEffect(() => { load(); }, [load]);

  const assign = async (formId, prospectHidden = false) => {
    setBusyId(formId);
    setError(null);
    try {
      await apiRequest(`/api/forms/${formId}/rooms`, { token: authToken, method: 'POST', body: { buyerSpaceId: spaceId, prospectHidden } });
      setPickerOpen(false);
      await load();
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const unassign = async (form) => {
    if (!window.confirm(`Remove "${form.name}" from this room? Answers already given are kept on the form.`)) return;
    setBusyId(form.id);
    try {
      await apiRequest(`/api/forms/${form.id}/rooms/${spaceId}`, { token: authToken, method: 'DELETE' });
      await load();
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleResponses = async (form) => {
    if (expandedId === form.id) { setExpandedId(null); return; }
    setExpandedId(form.id);
    if (responses[form.id]) return;
    try {
      const r = await apiRequest(`/api/forms/${form.id}/responses?buyerSpaceId=${encodeURIComponent(spaceId)}`, { token: authToken });
      setResponses((prev) => ({ ...prev, [form.id]: r }));
    } catch (err) {
      setError(err.message);
    }
  };

  const assignedIds = new Set((assigned || []).map((f) => f.id));
  const available = library.filter((f) => !assignedIds.has(f.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--room-text-muted, #64748b)' }}>
        Forms the buyer can fill in from this room. Adding one turns the Forms section on for the prospect.
      </p>
      {error && <div role="alert" style={alertStyle}>{error}</div>}

      {assigned === null ? (
        <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>
      ) : assigned.length === 0 ? (
        <div style={cardStyle}><p style={{ margin: 0, color: 'var(--room-text-muted, #64748b)' }}>No forms in this room yet.</p></div>
      ) : assigned.map((f) => {
        const r = responses[f.id];
        const count = f.responseCount ?? (r ? r.responses.length : null);
        return (
          <div key={f.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{f.name}</div>
                <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
                  {f.prospectHidden ? 'Hidden from the buyer' : 'Visible to the buyer'}{count != null ? ` · ${count} response${count === 1 ? '' : 's'} here` : ''}
                </div>
              </div>
              <button type="button" onClick={() => assign(f.id, !f.prospectHidden)} disabled={busyId === f.id} style={ghostBtn}>{f.prospectHidden ? 'Show to buyer' : 'Hide from buyer'}</button>
              <button type="button" onClick={() => toggleResponses(f)} style={ghostBtn}>{expandedId === f.id ? 'Hide answers' : 'Answers'}</button>
              <button type="button" onClick={() => unassign(f)} disabled={busyId === f.id} style={{ ...ghostBtn, color: '#dc2626' }}>Remove</button>
            </div>
            {expandedId === f.id && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--room-border-soft, #f1f5f9)', paddingTop: 12 }}>
                {!r ? (
                  <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading…</div>
                ) : r.responses.length === 0 ? (
                  <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Nobody in this room has answered yet.</div>
                ) : r.responses.map((resp) => (
                  <div key={resp.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      <strong>{resp.respondentName || resp.respondentEmail || 'Someone'}</strong>
                      <span style={{ color: 'var(--room-text-subtle, #94a3b8)' }}> · {formatWhen(resp.submittedAt)}</span>
                    </div>
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 2fr', gap: '4px 12px', fontSize: 13 }}>
                      {(resp.answers || []).map((a) => (
                        <Fragment key={a.fieldId}>
                          <dt style={{ color: 'var(--room-text-muted, #64748b)' }}>{a.label}</dt>
                          <dd style={{ margin: 0, color: 'var(--room-text-body, #334155)' }}>
                            {a.type === 'loss_reason' ? (resp.lossReasonLabel || String(a.value)) : Array.isArray(a.value) ? a.value.join(', ') : String(a.value)}
                          </dd>
                        </Fragment>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {pickerOpen ? (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Add a form to this room</div>
          {available.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>Every active form is already in this room. Create or activate one on the Forms page.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {available.map((f) => (
                <button key={f.id} type="button" onClick={() => assign(f.id)} disabled={busyId === f.id} style={{ ...ghostBtn, textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{f.name}</span><span style={{ color: 'var(--room-text-subtle, #94a3b8)', fontWeight: 400 }}>{f.kind.replace('_', '/')}</span>
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setPickerOpen(false)} style={{ ...ghostBtn, marginTop: 10 }}>Cancel</button>
        </div>
      ) : (
        <div><button type="button" onClick={() => setPickerOpen(true)} style={primaryBtn}>+ Add form</button></div>
      )}
    </div>
  );
}

const cardStyle = { background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 16, color: 'var(--room-text-strong, #0f172a)' };
const ghostBtn = { padding: '8px 14px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const primaryBtn = { padding: '10px 18px', borderRadius: 10, border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const alertStyle = { padding: '10px 12px', borderRadius: 10, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', fontSize: 14 };
